import { UserDashboardsRepo } from "@/repos/UserDashboardsRepo";
import { DashboardRoundSummaryModel, UserDashboardModel } from "@/models/UserDashboardModels";
import { RoundModel } from "@/models/RoundModels";
import { CourseService } from "./CourseService";
import { Timestamp } from "firebase/firestore";
import { LeagueMemberResultModel, LeagueResultModel } from "@/models/LeagueResultModels";
import { LeagueModel, LeagueScheduleModel } from "@/models/LeagueModels";
import { CourseModel, HoleInfoModel } from "@/models/CourseModels";
import { calcNetScore } from "@/utils/handicapUtils";
import { RoundService } from "./RoundService";
import { resolveType } from "./ServiceContainer";
import { LeagueResultService } from "./LeagueResultService";

// src/services/UserDashboardService.ts
export class UserDashboardService {
    constructor(private userDashboardRepo: UserDashboardsRepo, private courseService: CourseService) { }

    async get(id: string): Promise<UserDashboardModel | null> {
        return await this.userDashboardRepo.getById(id);
    }

    async create(item: UserDashboardModel): Promise<void> {
        return await this.userDashboardRepo.create(item, item.userId);
    }

    async update(id: string, updates: Partial<UserDashboardModel>): Promise<void> {
        return await this.userDashboardRepo.update(id, updates);
    }

    /**
     * Find all user dashboards that contain league rounds matching the specified leagueId and dayId
     */
    async findByLeagueDay(leagueId: string, dayId: string): Promise<UserDashboardModel[]> {
        return await this.userDashboardRepo.findByLeagueDay(leagueId, dayId);
    }

    /**
     * Recalculate net score in round and dashboard (and update if needed), then copy round data from user dashboards to league result model members
     * Also creates missing leagueRound entries in userDashboards for league members who don't have them
     * Finally, copies handicap, flightId, and handicapCalcSteps from leagueResult to userDashboard
     */
    async copyRoundDataToLeagueResult(
        leagueId: string,
        dayId: string,
        courseModel: CourseModel,
        leagueModel: LeagueModel,
        scheduleModel: LeagueScheduleModel,
        leagueResultModel: LeagueResultModel,
        onProgress?: (current: number, total: number, message: string) => void
    ): Promise<Record<string, LeagueMemberResultModel>> {
        const dashboards = await this.findByLeagueDay(leagueId, dayId);
        console.log('dashboards', dashboards);
        const memberData: Record<string, LeagueMemberResultModel> = {};

        const roundService = resolveType(RoundService);
        const totalDashboards = dashboards.length;
        let currentDashboard = 0;

        const coursePar = courseModel?.scorecard?.holes.reduce((sum, hole) => sum + (hole.par || 0), 0) || 0

        for (const dashboard of dashboards) {
            currentDashboard++;
            console.log('processing dashboard', dashboard.userId, dashboard.encodedEmail);
            if (onProgress) {
                onProgress(currentDashboard, totalDashboards, 'processing dashboard ' + dashboard.userId + ' ' + dashboard.encodedEmail);
            }
            const leagueRounds = dashboard.leagueRounds[leagueId];
            if (!leagueRounds) continue;

            let dashboardRound = leagueRounds.rounds.find(round =>
                round.setup?.league?.id === leagueId &&
                round.setup?.league?.dayId === dayId
            );
            let dashboardRoundChanged = false;

            const leagueMember = leagueModel?.members[dashboard.encodedEmail || ''];
            const leagueResultMember = leagueResultModel?.members[dashboard.encodedEmail || ''];

            if (!dashboardRound) {
                // no round found, user did not play this day.
                // create an entry in the user dashboard for the day
                const teeName = leagueModel?.flights.find(f => f.id == leagueMember!.flightId)?.teeName || '';
                dashboardRound = {
                    roundId: '',
                    courseId: courseModel?.id || '',
                    courseName: courseModel?.name || '',
                    startedAt: null,
                    netScore: 0,
                    par: coursePar || null,
                    holesPlayed: 0,
                    holesToPlay: scheduleModel?.holesToPlay || 'front9',
                    handicap: leagueResultMember?.handicap || leagueMember?.handicap || null,
                    handicapCalcSteps: leagueResultMember?.handicapCalcSteps || null,
                    flightId: leagueResultMember?.flightId || leagueMember?.flightId || leagueMember?.initialFlightId || null,
                    holes: [],
                    playedAt: null,
                    grossScore: 0,
                    grossScoreAdjusted: 0,
                    selectedTee: teeName,
                    status: 'completed',
                    stats: {
                        fairwaysHit: null,
                        greensInRegulation: null,
                        putts: null,
                        chips: null,
                        penalties: null
                    },
                    setup: {
                        type: 'league',
                        league: {
                            id: leagueId,
                            dayId: dayId
                        }
                    }
                };
                leagueRounds.rounds.push(dashboardRound!);

                console.log('created missing dashboardRound for ', dashboard.userId, dashboard.encodedEmail);

                dashboardRoundChanged = true;

            }

            if (dashboardRound!.status !== 'completed') {
                dashboardRound!.status = 'completed';
                dashboardRoundChanged = true;
            }

            if (dashboardRound!.handicap !== leagueResultMember?.handicap && leagueResultMember?.handicap !== null && leagueResultMember?.handicap !== undefined) {
                console.log('updating handicap for ', dashboard.userId, dashboardRound!.handicap, (leagueResultMember?.handicap || '--missing--'));
                dashboardRound!.handicap = leagueResultMember?.handicap || null;
                dashboardRoundChanged = true;
            }

            if (dashboardRound!.flightId !== leagueResultMember?.flightId && leagueResultMember?.flightId !== null && leagueResultMember?.flightId !== undefined) {
                console.log('updating flightId for ', dashboard.userId, dashboardRound!.flightId, (leagueResultMember?.flightId || '--missing--'));
                dashboardRound!.flightId = leagueResultMember?.flightId || null;
                dashboardRoundChanged = true;
            }

            if (leagueResultMember?.handicapCalcSteps !== null && leagueResultMember?.handicapCalcSteps !== undefined && dashboardRound!.handicapCalcSteps?.join(',') !== leagueResultMember?.handicapCalcSteps.join(',')) {
                console.log('updating handicapCalcSteps for ', dashboard.userId, dashboardRound!.handicapCalcSteps, (leagueResultMember?.handicapCalcSteps || '--missing--'));
                dashboardRound!.handicapCalcSteps = leagueResultMember?.handicapCalcSteps || [];
                dashboardRoundChanged = true;
            }


            // first, let's recalculate their net score in case handicap changed at some point.
            let round = dashboardRound!.roundId ? await roundService.get(dashboardRound!.roundId) : null;
            if (!round) {
                // if roundId isn't set right, we need to do a collection query to try to find the appropriate round document.
                const rounds = await roundService.findByLeagueDayAndUser(leagueId, dayId, dashboard.userId);
                round = rounds.find(r => r.setup?.league?.id === leagueId && r.setup?.league?.dayId === dayId && r.users[dashboard.userId]) || null;
            }
            const roundUser = round?.users[dashboard.userId];
            let roundChanged = false;

            if (round && dashboardRound && round.users[dashboard.userId]) {
                dashboardRound!.holes = round.users[dashboard.userId].holeStats.map(holeStat => ({
                    hole: holeStat.hole,
                    grossScore: holeStat.grossScore || null,
                    grossScoreAdjusted: holeStat.grossScoreAdjusted || null,
                    netScore: holeStat.netScore || null,
                    par: courseModel?.scorecard?.holes.find(h => h.number == holeStat.hole)?.par || holeStat.par || 0,
                    fairwayHit: holeStat.fairwayHit || null,
                    greenInRegulation: holeStat.greenInRegulation || null,
                    putts: holeStat.putts || null,
                    chips: holeStat.chips || null,
                    penalties: holeStat.penalties || null
                }));
                dashboardRoundChanged = true;
            }

            dashboardRound!.holes.forEach(hole => {

                if (hole.par <= 0 || !hole.par) {
                    hole.par = courseModel?.scorecard?.holes.find(h => h.number == hole.hole)?.par || 0;
                    dashboardRoundChanged = true;
                }

                // the only score related value we trust is grossScore. derive everything else from that.
                const newGrossAdjusted = Math.min(hole.par + 2, hole.grossScore || 0);
                if (newGrossAdjusted !== hole.grossScoreAdjusted) {
                    hole.grossScoreAdjusted = newGrossAdjusted;
                    dashboardRoundChanged = true;
                }

                const holeInfo = courseModel?.scorecard?.holes.find(h => h.number == hole.hole) as HoleInfoModel;
                const newNetScore = calcNetScore({ holesToPlay: dashboardRound!.holesToPlay, grossScore: hole.grossScore || 0, holeInfo: holeInfo, handicap: dashboardRound!.handicap || 0 }) || 0;
                if (newNetScore !== hole.netScore) {
                    hole.netScore = newNetScore;
                    dashboardRoundChanged = true;
                }
                if (round && roundUser) {
                    const roundHole = roundUser.holeStats.find(h => h.hole == hole.hole);
                    if (roundHole && roundHole.grossScoreAdjusted !== hole.grossScoreAdjusted) {
                        roundHole.grossScoreAdjusted = hole.grossScoreAdjusted;
                        roundChanged = true;
                    }
                    if (roundHole && roundHole.netScore !== hole.netScore) {
                        roundHole.netScore = hole.netScore;
                        roundChanged = true;
                    }
                }

            });

            const newHolesPlayed = dashboardRound!.holes.filter(hole => hole.grossScore !== null && hole.grossScore !== 0).length;
            if (newHolesPlayed !== dashboardRound!.holesPlayed) {
                dashboardRound!.holesPlayed = newHolesPlayed;
                dashboardRoundChanged = true;
            }

            const newTotalGrossScoreAdjusted = dashboardRound!.holes.reduce((sum, hole) => sum + (hole.grossScoreAdjusted || 0), 0);
            if (newTotalGrossScoreAdjusted !== dashboardRound!.grossScoreAdjusted) {
                dashboardRound!.grossScoreAdjusted = newTotalGrossScoreAdjusted;
                dashboardRoundChanged = true;
            }

            const newTotalNetScore = dashboardRound!.holes.reduce((sum, hole) => sum + (hole.netScore || 0), 0);
            if (newTotalNetScore !== dashboardRound!.netScore) {
                dashboardRound!.netScore = newTotalNetScore;
                dashboardRoundChanged = true;
            }

            if (round && roundChanged) {
                // use dashboard handicap if it exists, otherwise use round handicap if it exists, otherwise use league member handicap
                if (roundUser) {
                    roundUser!.handicap = leagueResultMember?.handicap || dashboardRound!.handicap || roundUser!.handicap || 0;
                }
                console.log('updating round for ', dashboard.userId, dashboard.encodedEmail, dashboardRound!.roundId);
                await roundService.update(dashboardRound!.roundId, dashboard.userId, round!, courseModel);
            }

            if (dashboardRoundChanged) {
                console.log('updating dashboard for ', dashboard.encodedEmail);
                await this.userDashboardRepo.update(dashboard.userId, dashboard);
            }


            // Extract user email from dashboard userId (assuming userId is the encoded email)
            const encodedEmail = dashboard.encodedEmail;

            // Skip dashboards without a valid encodedEmail to prevent empty string keys
            if (!encodedEmail || encodedEmail.trim() === '') {
                console.warn(`Skipping updating LeagueResultModel for user ${dashboard.userId} - missing or empty encodedEmail`);
                continue;
            }

            // Create member result data from the round
            const memberResult: LeagueMemberResultModel = {
                uid: dashboard.userId,
                encodedEmail: encodedEmail,
                holeStats: dashboardRound!.holes || [],
                holesCompleted: dashboardRound!.holesPlayed || 0,
                teeName: dashboardRound!.selectedTee || '',
                flightId: dashboardRound!.flightId || '',
                handicap: dashboardRound!.handicap || 0,
                prevFlightId: leagueMember?.flightId || null,
                prevHandicap: leagueMember?.handicap || null,
                handicapCalcSteps: [],
                grossScore: dashboardRound!.grossScore || 0,
                grossScoreAdjusted: dashboardRound!.grossScoreAdjusted || 0,
                grossScoreRanked: 0, // Will be calculated later
                grossRank: 0, // Will be calculated later
                grossRankText: '', // Will be calculated later
                netScore: dashboardRound!.netScore || 0,
                netScoreRanked: 0, // Will be calculated later
                netRank: 0, // Will be calculated later
                netRankText: '', // Will be calculated later
                payouts: [], // Will be calculated later
                roundId: dashboardRound!.roundId || null,
                totalLeaguePayout: 0, // Will be calculated later
                totalGrossSkinsPayout: 0, // Will be calculated later
                totalNetSkinsPayout: 0, // Will be calculated later
                totalPayout: 0, // Will be calculated later
            };

            memberData[encodedEmail] = memberResult;
        }

        return memberData;
    }


    async updateForRound(round: RoundModel, userId: string, courseModel: CourseModel | null): Promise<void> {
        const course = courseModel || await this.courseService.get(round.setup.courseId);
        const userData = round.users[userId];
        if (!userData) {
            throw new Error('User data not found');
        }

        const courseHoles = course?.scorecard?.holes;

        const setup = round.setup;
        let handicap = userData.handicap;
        let flightId = userData.flightId;
        let handicapCalcSteps: string[] = [];

        if (setup?.league?.id && setup?.league?.dayId) {
            const leagueResultService = resolveType(LeagueResultService);
            const leagueResult = await leagueResultService.get(`${setup.league.id}_${setup.league.dayId}`);
            if (leagueResult) {
                // if the user is in the leagueResult, use the handicap and flightId from the leagueResult
                handicap = leagueResult.members[userId]?.handicap || handicap;
                flightId = leagueResult.members[userId]?.flightId || flightId;
                handicapCalcSteps = leagueResult.members[userId]?.handicapCalcSteps || handicapCalcSteps;
            }
        }

        // Create dashboard round summary
        const summary: DashboardRoundSummaryModel = {
            roundId: round.id,
            courseId: round.setup.courseId,
            courseName: round.setup.courseName,
            playedAt: round.completedAt || round.updatedAt || Timestamp.now(),
            startedAt: round.createdAt || Timestamp.now(),
            grossScore: userData.holeStats.reduce((sum, hole) => sum + (hole.grossScore || 0), 0),
            grossScoreAdjusted: userData.holeStats.reduce((sum, hole) => sum + Math.min(hole.par + 2, hole.grossScore || 0), 0),
            netScore: userData.holeStats.reduce((sum, hole) => sum + (hole.netScore || 0), 0),
            holesPlayed: userData.holeStats.filter(hole => hole.grossScore !== null && hole.grossScore !== 0).length,
            holesToPlay: round.setup.holesToPlay,
            selectedTee: userData.selectedTee || '',
            par: userData.holeStats.reduce((sum, hole) => sum + (hole.par || 0), 0) || null,
            status: round.status || 'active',
            handicap: handicap || null,
            handicapCalcSteps: handicapCalcSteps || null,
            flightId: flightId || null,
            holes: userData.holeStats.map(hole => ({
                hole: hole.hole,
                grossScore: hole.grossScore || null,
                grossScoreAdjusted: Math.min(hole.par + 2, hole.grossScore || 0),
                netScore: hole.netScore || null,
                par: courseHoles && courseHoles[hole.hole - 1] ? courseHoles[hole.hole - 1].par : 0,
                mensHandicap9: courseHoles && courseHoles[hole.hole - 1] ? courseHoles[hole.hole - 1].mensHandicap9 : null,
                fairwayHit: hole.fairwayHit || null,
                greenInRegulation: hole.greenInRegulation || null,
                putts: hole.putts || null,
                chips: hole.chips || null,
                penalties: hole.penalties || null
            })),
            stats: {
                fairwaysHit: userData.holeStats.filter(hole => hole.fairwayHit === true).length || null,
                greensInRegulation: userData.holeStats.filter(hole => hole.greenInRegulation === true).length || null,
                putts: userData.holeStats.reduce((sum, hole) => sum + (hole.putts || 0), 0) || null,
                chips: userData.holeStats.reduce((sum, hole) => sum + (hole.chips || 0), 0) || null,
                penalties: userData.holeStats.reduce((sum, hole) => sum + (hole.penalties || 0), 0) || null
            },
            setup: {
                type: setup.type || 'casual',
                ...(setup.league && { league: setup.league })
            }
        };
        let userDashboardModel = await this.userDashboardRepo.getById(userId);
        if (!userDashboardModel) {
            userDashboardModel = {
                userId: userId,
                updatedAt: Timestamp.now(),
                casualRounds: [],
                leagueRounds: {},
                tournamentRounds: [],
            }
        }

        // console.log('summary', summary);

        if (summary.setup.type === 'casual') {
            userDashboardModel!.casualRounds = [summary, ...userDashboardModel!.casualRounds].slice(0, 50);
        } else if (summary.setup.type === 'league' && summary.setup.league?.id) {
            if (!userDashboardModel!.leagueRounds[summary.setup.league.id]) {
                userDashboardModel!.leagueRounds[summary.setup.league.id] = {
                    leagueName: summary.setup.league.id, // Use leagueId as name for now
                    rounds: []
                };
            }

            // console.log('before existingRounds', userDashboardModel);
            // Overwrite any existing round with the same dayId
            const existingRounds = userDashboardModel!.leagueRounds[summary.setup.league!.id]?.rounds;
            if (existingRounds) {
                const newRounds = existingRounds.filter(r => r.setup?.league?.dayId !== summary.setup.league!.dayId);
                userDashboardModel!.leagueRounds[summary.setup.league!.id].rounds = [summary, ...newRounds].slice(0, 50);
            } else {
                userDashboardModel!.leagueRounds[summary.setup.league!.id].rounds = [summary];
            }

            // console.log('after existingRounds', userDashboardModel);
        }

        delete userDashboardModel!.activeRoundId;
        userDashboardModel!.updatedAt = Timestamp.now();

        return await this.userDashboardRepo.update(userId, userDashboardModel!);
    }

    async removeRound(userId: string, roundId: string): Promise<void> {
        const userDashboardModel = await this.userDashboardRepo.getById(userId);
        if (!userDashboardModel) {
            throw new Error('User dashboard not found');
        }

        delete userDashboardModel.activeRoundId;


        // find any item in leagueRounds that has a round with the same roundId and remove it
        userDashboardModel.leagueRounds = Object.fromEntries(
            Object.entries(userDashboardModel.leagueRounds).map(([leagueId, league]) => [
                leagueId,
                {
                    ...league,
                    rounds: league.rounds.filter(r => r.roundId !== roundId)
                }
            ])
        );


        // find any item in casualRounds that has a round with the same roundId and remove it
        userDashboardModel.casualRounds = userDashboardModel.casualRounds.filter(r => r.roundId !== roundId);

        // find any item in tournamentRounds that has a round with the same roundId and remove it
        userDashboardModel.tournamentRounds = userDashboardModel.tournamentRounds.filter(r => r.roundId !== roundId);

        console.log('userDashboardModel to update:', userDashboardModel);

        await this.userDashboardRepo.update(userId, userDashboardModel);
    }

    async delete(id: string): Promise<void> {
        return await this.userDashboardRepo.delete(id);
    }
}